import styled from '@emotion/styled';
import { Loader } from '@vmos2/design-system';
import { storeLocation, useLocation } from '@vmos2/location';
import { useInfiniteScroll } from '@vmos2/utils';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Store } from './store';
import { Unavailable } from './Unavailable';

type Props = {
  brandUUIDS: [],
  channel: string,
  clearStoreMenus: Function,
  deliveryIntegration: mixed,
  getStores: Function,
  getStoresMenus: Function,
  hasEnabledStores: boolean,
  isGeocodeEnabled?: boolean,
  location?: {},
  menus: {},
  postCode?: string,
  settings: {},
  stores: [],
  storesCount: Number,
};

export const Stores = ({
  brandUUIDS,
  channel,
  clearStoreMenus,
  deliveryIntegration,
  getStores,
  getStoresMenus,
  hasEnabledStores,
  isGeocodeEnabled,
  location,
  menus,
  postCode,
  settings,
  stores,
  storesCount,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const { isGoogleMapsEnabled } = useLocation();
  const getOffset = useCallback(() => storesCount, [storesCount]);

  const fetchStores = useCallback(
    async (offset = 0) => {
      if (!hasEnabledStores) {
        return [];
      }

      setLoading(true);

      try {
        const value = await getStores(
          {
            offset,
            postcode: get(location, 'postalcode', postCode),
            limit: 15,
            sortBy: ['name', 'sortOrder', !isEmpty(location) && 'distance'].filter(Boolean),
            lat: get(location, 'latitude'),
            long: get(location, 'longitude'),
            sortDir: 'ASC',
            status: '1',
          },
          { isGoogleMapsEnabled, deliveryIntegration }
        );

        getStoresMenus({
          stores: map(value, 'uuid'),
          locale,
          brands: brandUUIDS,
        });

        return value;
      } finally {
        setLoading(false);
      }
    },
    [location, channel, hasEnabledStores, locale]
  );

  const storesWithLocation = useMemo(() => {
    return sortBy(
      stores.map(store => storeLocation(store, location, postCode)),
      'kmWithoutText'
    );
  }, [stores, location]);

  useInfiniteScroll(fetchStores, { getOffset, deps: [location, hasEnabledStores, channel, locale] });

  useEffect(() => {
    clearStoreMenus();
  }, [locale]);

  return (
    <Wrapper data-test="stores">
      {!storesWithLocation.length && !loading && (
        <Unavailable hasSelectors channel={channel} hasEnabledStores={hasEnabledStores} />
      )}

      {map(storesWithLocation, store => (
        <Store
          isGeocodeEnabled={isGeocodeEnabled}
          key={store.uuid}
          location={location}
          menus={menus[store.uuid]}
          settings={settings}
          store={store}
        />
      ))}

      {loading && <Loader {...(storesCount && { mt: 8 })} position="relative" top="0" />}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.constants.headerHeight};
`;
